import {RenderingPrioritizerBoundary} from '@aboutyou/data-fetcher';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {
  ProductMiniAdpWithBasketFlyout,
  SizeSelectionOpenedStateKey,
  WishlistHeader,
  WishlistHeaderBanner,
  WishlistItemStream,
  WishlistNavigationTree,
  WishlistPageMeta,
  WishlistSizeReminder,
  createSimilarProductsPageUrl,
  useMiniAdpDialogOpenedState,
  useSimilarProductsPageOriginClick,
  useSizeReminderPopupOpenedState,
} from '@aboutyou/logic-components';
import {ScrollTopAnchor, useHistory} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import {RowSection, SplitView, Z_INDEXES} from '@aboutyou/ui-components';
import {useClearWishlistBubble, useWishlistCount} from '@aboutyou/user';
import React, {useCallback, useEffect, useRef} from 'react';
import {Sticky} from 'react-stickup-ay';
import styled from 'styled-components';

export const WishlistPageEager = () => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const topanchorRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const clearWishlistBubbleCallback = useClearWishlistBubble();

  const trackFeature = useTrackFeature();
  const trackSimilarPageOriginClick = useSimilarProductsPageOriginClick();
  const {open: openMiniAdp} = useMiniAdpDialogOpenedState();

  const {open: openSizeReminder, parameter: sizeReminderParam} =
    useSizeReminderPopupOpenedState();

  useEffect(() => {
    clearWishlistBubbleCallback();
  }, [clearWishlistBubbleCallback]);

  const handleOnClickAddToBasket = useCallback(
    ({productId}: {productId: number}) => {
      trackFeature({eventType: 'click', label: 'WL_A2B'});
      openMiniAdp(productId);
    },
    [],
  );

  const handleOnClickSoldoutCta = useCallback((productId: number) => {
    openSizeReminder({parameter: `${productId}`});
  }, []);

  const onSimilarProductsClick = useCallback((product: Product) => {
    trackFeature({eventType: 'click', label: 'WL_similar_products'});
    trackSimilarPageOriginClick();
    history.push(createSimilarProductsPageUrl(product));
  }, []);

  const {count: wishlistCount} = useWishlistCount();
  const isWishlistEmpty = wishlistCount === 0;

  const {isActive: interactiveHoverMode} = useIsFeatureActive(
    'desk_interactive_tile_wishlist',
    {
      abTest: true,
    },
  );

  return (
    <RowSection>
      <WishlistPageMeta />

      <ScrollTopAnchor
        anchorRef={topanchorRef}
        as={StyledSplitView}
        split={'30:70'}
        splitMedium={'20:80'}
        splitLarge={'15:85'}
        disableSplit={isWishlistEmpty}
        refStart={sidebarRef}
        contentStart={
          !isWishlistEmpty && (
            <Sticky
              overflowScroll="flow"
              experimentalNative
              container={sidebarRef}
              style={{zIndex: Z_INDEXES.CATEGORY_TREE}}
            >
              <StyledWishlistNavigationTree excludeUrlParams={['sort']} />
            </Sticky>
          )
        }
        contentEnd={
          <StyledSection data-increase-padding={interactiveHoverMode}>
            <RenderingPrioritizerBoundary>
              <WishlistHeaderBanner renderingPriority={1} />
              {!isWishlistEmpty && <WishlistHeader filterVariant="dropdown" />}
              <WishlistItemStream
                onClickAddToBasket={handleOnClickAddToBasket}
                onClickSoldoutCta={handleOnClickSoldoutCta}
                onSimilarProductsClick={onSimilarProductsClick}
                interactiveHoverMode={interactiveHoverMode}
                productTileGalleryMode={
                  interactiveHoverMode ? 'hover' : undefined
                }
                renderingPriority={2}
              />
            </RenderingPrioritizerBoundary>

            <ProductMiniAdpWithBasketFlyout
              sizeSelectionStateManagerKey={
                SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
              }
            />
          </StyledSection>
        }
      />
      <WishlistSizeReminder productId={Number(sizeReminderParam ?? '')} />
    </RowSection>
  );
};

const StyledSection = styled.section`
  &[data-increase-padding='true'] {
    padding-bottom: 1rem;
  }
`;

const StyledSplitView = styled(SplitView)`
  padding-top: 28px;
`;

const StyledWishlistNavigationTree = styled(WishlistNavigationTree)`
  margin: 1.375rem 0;
  word-break: break-word;
`;
